import React, { RefObject, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useConnectMeetingDisabled from '../../../../hooks/use-connect-meeting-disabled';
import API, { PROTECTED } from '../../../../lib/api';
import { JOIN_STATUS } from '../../../../lib/constants';
import { EventType } from '../LayoutWrapper';
import { Button, ButtonProps } from '../../../../components/_base/Button';
import Icon from '../../../../components/_base/Icon';
import Buttons from '../../../../components/button';
import useMeetingCalendar from '../../../../hooks/use-meeting-calendar';

export interface IFooterProps {
  meetingId: string,
  joinStatus?: string,
  featured: boolean,
  access: string,
  startDateTime: string;
  event: EventType,
  changeJoinStatus: (newJoinStatus: string) => void
}

const FooterButtons = ({
  event,
  access,
  featured,
  meetingId,
  joinStatus,
  startDateTime,
  changeJoinStatus
}: IFooterProps): JSX.Element => {
  const history = useHistory();
  const [status, setJoinStatus] = useState(joinStatus);
  const { attendMeeting, leaveMeeting } = API[PROTECTED];

  const isConnectBtnDisabled = useConnectMeetingDisabled(event, startDateTime);
  const isAddBtnShown = access === 'OPEN';

  const { fetchOutlookCalendarForMeeting, fetchGoogleCalendarForMeeting } = useMeetingCalendar();

  const eventIdString = event.eventId.toString();
  const eventName = event.name || '';

  const className = 'editable-session';
  const classNameSessionTableCellButtons = `${className}--table-cell-buttons flex ${featured ? 'flex-row items-center h-10 ' : 'flex-col justify-start'}`;
  const classNameSessionTableCellBtnAdd = `${className}--table-cell-btn-add`;
  const classNameSessionTableCellBtnJoin = `${className}--table-cell-btn-join`;

  const featuredMeetingButtonStyles = {
    width: featured ? '120px' : '100px',
    minWidth: featured ? '120px' : '100px',
    height: featured ? '40px' : '30px',
    marginLeft: featured ? '0' : '0.75rem',
    marginTop: '0.25rem',
    marginBottom: '0.25rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
  };

  const getAddRemoveBtnClassName = (): string => {
    let addRemoveBtnClassName = 'font-size-11px font-semibold add-remove-btn outline-auto-visible  ';
    featured && (addRemoveBtnClassName += 'session-meeting-title-add-rm-btn grid items-center rounded my-5 py-2 bg-white');
    addRemoveBtnClassName += featured
      ? 'session-meeting-title-add-rm-btn grid items-center rounded my-5 py-2 bg-white border border-gray-400 '
      : 'border-t p-2 w-full hover:bg-gray-100';
    addRemoveBtnClassName += status === JOIN_STATUS.JOINED_AS_REGISTRANT ? ' hover-label-btn' : '';
    addRemoveBtnClassName += status === JOIN_STATUS.JOINED_AS_REGULAR ? ` cursor-not-allowed ${!featured ? 'bg-cool-gray' : ''}` : '';
    addRemoveBtnClassName += status !== JOIN_STATUS.NOT_JOINED && featured ? ' w-112px' : '';
    addRemoveBtnClassName += status === JOIN_STATUS.NOT_JOINED ? ' text-primary fill-primary' : '';
    addRemoveBtnClassName += status === JOIN_STATUS.NOT_JOINED && featured ? ' bg-white on-hover-shadow' : '';
    addRemoveBtnClassName += status === JOIN_STATUS.NOT_JOINED && !featured ? ' bg-cool-gray' : '';
    addRemoveBtnClassName += status === (JOIN_STATUS.JOINED_AS_REGISTRANT || JOIN_STATUS.JOINED_AS_REGULAR) && !featured ? ' bg-cool-gray' : '';
    addRemoveBtnClassName += ' ' + classNameSessionTableCellBtnAdd;
    return addRemoveBtnClassName;
  };

  const toggleGeneralSession = () => {
    const oldJoinStatus = status;
    let newJoinStatus = JOIN_STATUS.JOINED_AS_REGISTRANT;

    switch (oldJoinStatus) {
    case JOIN_STATUS.NOT_JOINED:
      attendMeeting(event.eventId.toString(), meetingId)
        .then(() => changeJoinStatus(newJoinStatus))
        .catch(() => setJoinStatus(oldJoinStatus));
      setJoinStatus(newJoinStatus);
      return;
    case JOIN_STATUS.JOINED_AS_REGISTRANT:
      newJoinStatus = JOIN_STATUS.NOT_JOINED;
      leaveMeeting(event.eventId.toString(), meetingId)
        .then(() => changeJoinStatus(newJoinStatus))
        .catch(() => setJoinStatus(oldJoinStatus));
      setJoinStatus(newJoinStatus);
      return;
    default: return;
    }
  };

  const getJoinBtnClassName = (): string => {
    let joinBtnClassName = 'font-size-11px rounded whitespace-no-wrap outline-auto-visible ';
    joinBtnClassName += featured ? 'session-meeting-title-join-btn-expanded p-2 my-1 ' : 'session-meeting-title-join-btn p-2 my-1 ';
    joinBtnClassName += isConnectBtnDisabled ? 'bg-white border border-gray-400 text-primary' : 'bg-primary text-white';
    joinBtnClassName += ' '+ classNameSessionTableCellBtnJoin;
    return joinBtnClassName;
  };

  const getAddRemoveBtnParams = (): ButtonProps => {
    const labelClassName = `inline-block align-middle truncate w-full ${!featured ? 'w-2/3' : ''} `;
    const fullLabelText = `Add to ${event.agendaLabel ?? 'My Schedule'}`;
    const shortLabelText = `Add`;
    const style = { lineHeight: '20px' };

    const addLabel =
      <div className={labelClassName} style={style}>
        <Icon.Add className='mr-1 align-top' viewBox="0 0 24 24"/>
        {featured ? fullLabelText : shortLabelText}
      </div>;
    const addedLabel =
      <span className={labelClassName + 'text-off-black'} style={style}>
        <Icon.Added className='mr-1 fill-off-black align-top' viewBox="0 0 24 24"/>Added {featured ? `to ${event.agendaLabel ?? 'My Schedule'}` : ''}
      </span>;
    const removeLabel =
      <span className={labelClassName} style={style}>
        <Icon.Remove className='mr-1 fill-off-black align-top' viewBox="1 1 18 18" height="16" width="16" />
        Remove {featured ? `from ${event.agendaLabel ?? 'My Schedule'}` : ''}
      </span>;

    const addRemoveBtn = status === JOIN_STATUS.NOT_JOINED
      ? addLabel
      : status === JOIN_STATUS.JOINED_AS_REGULAR
        ? addedLabel
        : <>{addedLabel}{removeLabel}</>;

    const addRemoveBtnAriaLabel = status === JOIN_STATUS.NOT_JOINED
      ? `Add to ${event.agendaLabel ?? 'My Schedule'}`
      : status === JOIN_STATUS.JOINED_AS_REGULAR
        ? `Added to ${event.agendaLabel ?? 'My Schedule'}`
        : `Remove from ${event.agendaLabel ?? 'My Schedule'}`;

    const addRemoveBtnParams: ButtonProps = {
      name: 'meetingItem',
      type: 'button',
      onClick: toggleGeneralSession,
      label: addRemoveBtn,
      className: getAddRemoveBtnClassName(),
      'data-meeting-id': meetingId,
      ariaLabel: addRemoveBtnAriaLabel
    };

    if (!featured && status === JOIN_STATUS.NOT_JOINED) {
      addRemoveBtnParams['data-tooltip'] = `Add to ${event.agendaLabel ?? 'My Schedule'}`;
      addRemoveBtnParams['data-tooltip-position'] = 'bottom';
    }
    return addRemoveBtnParams;
  };

  const AddToCalendarButton = (props: { isFeatured: boolean }) => {
    const { isFeatured } = props;
    const [customStyles, setCustomStyles] = useState<React.CSSProperties>({});

    const onShowDropDown = (buttonRef: RefObject<HTMLDivElement>) => {
      const buttonLeftPosition = buttonRef.current?.getBoundingClientRect().x || 0;

      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      if (buttonLeftPosition > document.body.clientWidth / 2) {
        setCustomStyles({ left: '-220px' });
      } else {
        setCustomStyles({});
      }
    };

    return access === 'PLENARY' || status !== JOIN_STATUS.NOT_JOINED ? (
      <Buttons.OptionsButton
        customStyles={!isFeatured ? customStyles : {}}
        onShowDropDown={onShowDropDown}
        label={'+ Add to Calendar'}
        name={'addToCalendar'}
        disabled={false}
        eventId={eventIdString}
        eventName={eventName}
        className='select-box--button-small'
        downloadIcsFile={()=> fetchOutlookCalendarForMeeting(eventIdString, meetingId, `${event.eventTitleForPassport || eventName}.ics`)}
        openGoogleCalendar={()=> fetchGoogleCalendarForMeeting(eventIdString, meetingId)}
        iconButton
      />
    ) : null;
  };

  const JoinButton = () =>
    <Button
      style={featuredMeetingButtonStyles}
      name='meetingItem'
      data-meeting-id={meetingId}
      type='button'
      onClick={() => {history.push(`/session/${event.eventId}/meeting/${meetingId}`);}}
      label={isConnectBtnDisabled ? 'View Details' : 'Join'}
      className={getJoinBtnClassName()}
      ariaLabel={isConnectBtnDisabled ? 'View Details' : 'Join Meeting'}
    />;

  const AddButton = () => isAddBtnShown ? (
    <Button
      style={{
        width: featured ? '45%' : '-webkit-fill-available',
        minWidth: featured ? '175px' : '-webkit-fill-available',
        height: featured ? '40px' : '33px',
        border: 'none',
        margin: '0',
      }}
      {...getAddRemoveBtnParams()}
    />
  ) : <div className={`${!featured ? 'bg-cool-gray' : ''}`} style={{ height: featured ? '40px' : '33px', width: '-webkit-fill-available', border: 'none' }} />;

  if (featured) {
    return (
      <div className={`${classNameSessionTableCellButtons}`}>
        <JoinButton />
        <div className='card-wrapper__buttons flex items-center justify-between w-full'>
          <AddButton />
          <AddToCalendarButton isFeatured={featured} />
        </div>
      </div>
    );
  }

  return (
    <div className={`${classNameSessionTableCellButtons}`}>
      <div className='card-wrapper__buttons card-wrapper__buttons--not-featured flex items-center justify-between'>
        <JoinButton />
        <AddToCalendarButton isFeatured={featured} />
      </div>
      <hr className='mt-2 w-full'/>
      <AddButton />
    </div>
  );
};

export { FooterButtons };